// Reward overlay: choice of N items (elite / boss caches) or major upgrades
// (after a sector boss). Launched by loot while GameScene is paused. Calls
// data.onPick(index | null) and then stops itself; loot resumes the game.

import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import type { RewardSceneData } from '../core/contracts';
import { getRun } from '../core/run';
import { session } from '../core/session';
import type { ItemComparison } from '../core/types';
import { reactorColorFor } from '../data/unlocks';
import { CardNavItem } from '../ui/cardNav';
import { CARD_FULL_W, createItemCard, createUpgradeCard, getCardHandle } from '../ui/ItemCard';
import {
  MenuNav,
  PAD,
  TYPE,
  UI,
  backdrop,
  footerHints,
  glowTexture,
  hairline,
  setMenuCursor,
  sfx,
  txt,
  vignetteTexture,
  type UiAction,
} from '../ui/kit';

const CARD_GAP = 44;
/** Cards taller than this are scaled down so the row always fits between title and footer. */
const MAX_CARD_H = 740;

export class RewardScene extends Phaser.Scene {
  private nav!: MenuNav;
  private cfg!: RewardSceneData;
  private cards: CardNavItem[] = [];
  private done = false;

  constructor() {
    super(SCENES.REWARD);
  }

  create(data: RewardSceneData): void {
    this.cfg = data;
    this.done = false;
    this.cards = [];
    // Launched by loot after GameScene raised the HUD: always draw above it.
    this.scene.bringToTop();
    setMenuCursor(this, true);

    const isUpgrade = !!data.upgrades?.length;
    const accent = isUpgrade ? COLORS.ultBar : this.mechAccent();

    // Dim the paused game; soft colour wash behind the cards.
    backdrop(this, 0.8);
    this.add.image(0, 0, vignetteTexture(this, 'ui_vignette_dark', 0x000000, 0.5, 0.85)).setOrigin(0, 0).setDisplaySize(UI.W, UI.H);
    this.add.image(UI.W / 2, 600, glowTexture(this)).setDisplaySize(UI.W * 1.1, 760).setTint(accent).setAlpha(0.1).setBlendMode(Phaser.BlendModes.ADD);

    // Title block.
    const kicker = txt(this, UI.W / 2, 70, isUpgrade ? 'MAJOR UPGRADE' : 'REWARD', { font: 'display', size: TYPE.label, weight: 700, color: accent, spacing: 8, originX: 0.5 });
    const title = txt(this, UI.W / 2, 100, (data.title || 'Choose a reward').toUpperCase(), {
      font: 'display',
      size: TYPE.title,
      weight: 700,
      spacing: 6,
      originX: 0.5,
      glow: { color: accent, blur: 18, alpha: 0.55 },
    });
    const line = hairline(this, UI.W / 2 - 420, 184, 840, accent, 0.8);
    const sub = txt(this, UI.W / 2, 198, data.subtitle ?? (isUpgrade ? 'Install one upgrade. It lasts for the rest of the run.' : 'Choose one. The others are lost.'), {
      size: TYPE.body,
      color: COLORS.uiDim,
      originX: 0.5,
    });

    // Cards.
    this.nav = new MenuNav(this, {
      layout: 'horizontal',
      onBack: () => this.skip(),
      keys: {
        Digit1: () => this.focusIndex(0),
        Digit2: () => this.focusIndex(1),
        Digit3: () => this.focusIndex(2),
        Digit4: () => this.focusIndex(3),
      },
      pad: { [PAD.START]: () => this.nav.activate() },
    });
    const containers = this.buildCards(data);
    const maxH = Math.max(...containers.map((c) => getCardHandle(c)?.height ?? 560));
    containers.forEach((c) => getCardHandle(c)?.setHeight(maxH));
    const n = containers.length;
    const fitW = (UI.W - 120 - CARD_GAP * (n - 1)) / Math.max(1, n);
    const scale = Math.min(1, MAX_CARD_H / maxH, fitW / CARD_FULL_W);
    const step = (CARD_FULL_W + CARD_GAP) * scale;
    const cy = 250 + (maxH * scale) / 2 + Math.max(0, (MAX_CARD_H - maxH * scale) / 2) * 0.5;
    containers.forEach((c, i) => {
      c.setPosition(UI.W / 2 + (i - (n - 1) / 2) * step, cy);
      const item = new CardNavItem(c, { onActivate: () => this.pick(i), baseScale: scale }).bind(this.nav);
      this.cards.push(item);
      // Slot number above each card (keyboard shortcut).
      const num = txt(this, c.x, cy - (maxH * scale) / 2 - 30, String(i + 1), { font: 'display', size: TYPE.label, weight: 700, color: COLORS.uiDim, originX: 0.5, originY: 0.5 });
      num.setAlpha(0);
      this.tweens.add({ targets: num, alpha: 0.8, delay: 300 + i * 90, duration: 300 });
    });

    // Footer.
    const hints: { action: UiAction; label: string }[] = [
      { action: 'navigateH', label: 'Select' },
      { action: 'confirm', label: isUpgrade ? 'Install' : 'Choose' },
    ];
    if (data.allowSkip) hints.push({ action: 'skip', label: 'Skip' });
    footerHints(this, hints, 'center');

    // Entrance: title drops in, cards rise one after another.
    for (const [i, o] of [kicker, title, sub].entries()) {
      o.setAlpha(0);
      this.tweens.add({ targets: o, alpha: 1, y: { from: o.y - 16, to: o.y }, delay: i * 60, duration: 320, ease: 'Cubic.easeOut' });
    }
    line.setScale(0, 1).setX(UI.W / 2);
    this.tweens.add({ targets: line, scaleX: 1, x: UI.W / 2 - 420, duration: 420, ease: 'Cubic.easeOut' });
    this.cards.forEach((c, i) => {
      const card = c.card;
      const y = card.y;
      card.setAlpha(0).setY(y + 80);
      this.tweens.add({ targets: card, alpha: 1, y, delay: 160 + i * 110, duration: 460, ease: 'Back.easeOut', easeParams: [1.1] });
    });
    this.time.delayedCall(160 + n * 110, () => this.nav.ensureFocus());
    this.nav.lock(420 + n * 110);
    sfx('loot_reveal', { volume: 0.8 });
  }

  update(): void {
    this.nav?.update();
  }

  private mechAccent(): number {
    const run = session.ctx?.run ?? getRun();
    try {
      return run ? reactorColorFor(run.mechId) : COLORS.volt;
    } catch {
      return COLORS.volt;
    }
  }

  private buildCards(data: RewardSceneData): Phaser.GameObjects.Container[] {
    if (data.upgrades?.length) {
      return data.upgrades.map((def) => createUpgradeCard(this, def, { controls: { confirm: 'Install' } }));
    }
    const items = data.items ?? [];
    return items.map((item, i) => {
      let comparison: ItemComparison | null = data.comparisons?.[i] ?? null;
      if (!comparison) {
        // The overlay can compute a comparison itself when the caller did not.
        try {
          const run = session.ctx?.run ?? getRun();
          comparison = run ? run.inventory.compare(item, run.mech.kitTags) : null;
        } catch {
          comparison = null;
        }
      }
      return createItemCard(this, item, { comparison, controls: { equip: 'Choose' } });
    });
  }

  private focusIndex(i: number): void {
    const c = this.cards[i];
    if (c) this.nav.focus(c);
  }

  private pick(i: number): void {
    if (this.done) return;
    this.done = true;
    this.nav.enabled = false;
    sfx('ui_confirm');
    const chosen = this.cards[i];
    this.cards.forEach((c, k) => {
      if (k === i) return;
      this.tweens.add({ targets: c.card, alpha: 0, y: c.card.y + 40, duration: 240, ease: 'Cubic.easeIn' });
    });
    // Flash the chosen card and lift it before closing.
    const flash = this.add.image(chosen.card.x, chosen.card.y, glowTexture(this)).setDisplaySize(620, 820).setBlendMode(Phaser.BlendModes.ADD).setTint(getCardHandle(chosen.card)?.color ?? 0xffffff).setAlpha(0);
    this.tweens.add({ targets: flash, alpha: { from: 0.55, to: 0 }, duration: 420 });
    this.tweens.add({ targets: chosen.card, scale: chosen.baseScale * 1.08, duration: 200, yoyo: true, ease: 'Cubic.easeOut' });
    this.time.delayedCall(380, () => this.finish(i));
  }

  private skip(): void {
    if (this.done) return;
    if (!this.cfg.allowSkip) {
      sfx('ui_deny');
      const f = this.nav.focused as CardNavItem | null;
      if (f) this.tweens.add({ targets: f.card, x: { from: f.card.x - 8, to: f.card.x }, duration: 260, ease: 'Elastic.easeOut' });
      return;
    }
    this.done = true;
    this.nav.enabled = false;
    sfx('ui_back');
    this.tweens.add({ targets: this.cards.map((c) => c.card), alpha: 0, duration: 200 });
    this.time.delayedCall(200, () => this.finish(null));
  }

  private finish(index: number | null): void {
    try {
      this.cfg.onPick(index);
    } catch (err) {
      console.error('[reward] onPick failed', err);
    }
    this.scene.stop();
  }
}

